import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  ASK_VYRILL_QUERY,
  GET_CONVERSATIONS,
} from "../../../Queries/ask-vyrill";
import { RootState } from "../AskVyrillReducer";
import { updateAskVyrillState } from "../Actions";
import { getDateString, splitDateRange } from "../../../utilities";
import "../styles/AskVyrillSearch.scss";
import moment from "moment";

interface AskBarSearchProps {
  isHistoryOpen?: boolean;
  renderFromChat?: boolean;
}

const AskBarSearch: React.FC<AskBarSearchProps> = ({
  isHistoryOpen,
  renderFromChat,
}) => {
  const [inputValue, setInputValue] = useState<string>("");
  const allConversations = useSelector(
    (state: RootState) => state.askVyrill?.allConversations
  );
  const messages = useSelector((state: RootState) => state.askVyrill.messages);
  const isNewChat = useSelector(
    (state: RootState) => state.askVyrill.isNewChat
  );
  const conversationId = useSelector(
    (state: RootState) => state.askVyrill.conversationId
  );
  const currentBrandId = useSelector(
    (state: any) => state.navbarStatsReducer.currentBrand._id
  );
  const custom_start_date = useSelector(
    (state: any) => state.askVyrill.custom_start_date
  );
  const custom_end_date = useSelector(
    (state: any) => state.askVyrill.custom_end_date
  );
  const custom_start_date_string = custom_start_date
    ? moment(custom_start_date).toISOString()
    : "";
  const custom_end_date_string = custom_end_date
    ? moment(custom_end_date).toISOString()
    : "";
  const messagesLoading = useSelector(
    (state: RootState) => state.askVyrill.messagesLoading
  );
  const askVyrillDuration = useSelector(
    (state: RootState) => state.askVyrill.askVyrillDuration
  );
  const inputRef = useRef<HTMLInputElement | null>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    loading,
    error,
    data,
    refetch: refetchAskVyrill,
  } = useQuery(ASK_VYRILL_QUERY, {
    skip: true,
    onError: (error) => {
      // Handle the error here
      dispatch(
        updateAskVyrillState({
          askVyrillTimoutError: true,
          messagesLoading: false,
        })
      );
    },
  });

  const {
    // loading: conversationLoading,
    // error: conversationError,
    // data: conversationData,
    refetch: refetchConversation,
  } = useQuery(GET_CONVERSATIONS, {
    variables: { brandId: currentBrandId },
    skip: true,
  });

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSearchPrompt = () => {
    const newQuestion = {
      content: inputValue,
      conversation_id: conversationId ? conversationId : null,
      role: "user",
      duration: askVyrillDuration?.value,
      start_date: splitDateRange(getDateString(askVyrillDuration?.value))
        ?.startDate,
      end_date: splitDateRange(getDateString(askVyrillDuration?.value))
        ?.endDate,
      _id: "",
    };
    if (inputValue) {
      dispatch(
        updateAskVyrillState({
          askVyrillError: false,
          messagesLoading: false,
          askVyrillTimoutError: false,
        })
      );
      if (renderFromChat) {
        // Send question to reducer on search submit

        const curent_time = new Date();
        const newChat = {
          brand_id: currentBrandId,
          title: inputValue,
          updated_at: curent_time,
          isNewChat: true,
        };

        dispatch(
          updateAskVyrillState({
            messages: [...messages, newQuestion],
            messagesLoading: true,
            ...(isNewChat && {
              allConversations: [newChat, ...allConversations],
            }),
            selectedPrompt: inputValue,
            // isFirstPrompt: false,
          })
        );
        // Fetch ask-API on seach submit
        refetchAskVyrill({
          brandId: currentBrandId,
          conversationId,
          duration: askVyrillDuration?.value,
          ...(askVyrillDuration.value === "custom" &&
          custom_start_date_string &&
          custom_end_date_string
            ? {
                start_date: custom_start_date_string,
                end_date: custom_end_date_string,
              }
            : {}),
          content: inputValue,
          role: "user",
        }).then((result: any) => {
          if (!result?.data?.askVyrill) {
            dispatch(
              updateAskVyrillState({
                askVyrillError: true,
                messagesLoading: false,
              })
            );
          } else {
            newQuestion._id = result?.data?.askVyrill?.question_id || ""; // adding question id in new question
            dispatch(
              updateAskVyrillState({
                messages: [...messages, newQuestion, result?.data?.askVyrill],
                messagesLoading: false,
              })
            );
            setInputValue("");
            inputRef.current?.focus();

            if (isNewChat) {
              refetchConversation().then((result: any) => {
                dispatch(
                  updateAskVyrillState({
                    allConversations: result?.data?.getConversations,
                    isNewChat: false,
                  })
                );
              });
            }
          }
        });
      } else {
        // Handling home ask-vyrill Modal
        const curent_time = new Date();
        const newConversation = {
          brand_id: currentBrandId,
          created_at: curent_time,
          title: inputValue,
          updated_at: curent_time,
          _id: "newConversation",
        };
        dispatch(
          updateAskVyrillState({
            messages: [newQuestion],
            selectedPrompt: inputValue,
            isFirstPrompt: true,
            messagesLoading: true,
            allConversations: [newConversation, ...allConversations],
          })
        );
        navigate(`/ask_vyrill`);
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearchPrompt();
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  return (
    <div className="AskVyrillSearch">
      <div className={`prompt-input`}>
        <input
          ref={inputRef}
          className={`ask-chat-vyrill-input ${
            isHistoryOpen ? "non-expanded-search-bar" : "expanded-search-bar"
          }`}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyPress}
          disabled={messagesLoading}
          placeholder="Ask Vyrill by typing your prompt here"
        />

        <img
          onClick={handleSearchPrompt}
          className="search-icon"
          src={require("@src/assets/images/vyrill/vyrill_logo.png").default}
          alt=""
          width={30}
        />
      </div>
    </div>
  );
};

export default AskBarSearch;
